import { useCallback, useEffect, useState } from 'react'
import { useParams } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Textarea } from '../ui/textarea'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { $api } from '../../api/queryClient'
import { apiClient } from '../../api/client'
import { refreshEntityIssues } from '../../hooks/useRefreshEntityIssues'
import { useDocumentTypes, useUploadDocument } from '../../hooks/useQueries'
import type { components } from '../../api/schema'
import { PROBLEM_CAUSES, PROBLEM_TYPES, type DeclarationProperties } from './declaration-fields'
import { VoiceNoteRecorder } from '../shared/VoiceNoteRecorder'
import { DeclarationPhotos, type PickedPhoto } from './DeclarationPhotos'

type TargetType = 'asset' | 'user' | 'contract' | 'document'

/**
 * What is being declared. The three differ only in the words used and in the
 * ticket they open — the questions asked are the same, on purpose: the person
 * filing does not know which of the three it is, and should not have to.
 */
export type DeclarationKind = 'damage' | 'breakdown' | 'incident'

type Props = {
    open: boolean
    onOpenChange: (open: boolean) => void
    kind: DeclarationKind
    targetType: TargetType
    targetEntityUid: string
    /** Name of the thing, used to compose the ticket title so the declarer
     * never types one. Absent for a generic incident, which asks instead. */
    subjectName?: string
    /** Where the voice note is filed. Only assets have a place for it. */
    assetUid?: string
    /** Analytics-ish provenance stamped into the ticket. */
    declaredFrom: DeclarationProperties['declared_from']
    onCreated?: (issue: components['schemas']['Issue']) => void
}

/** Document type code for a recorded declaration note. Resolved by code and
 * skipped when the organization has not been provisioned with it — an untyped
 * recording is better than a failed declaration. */
const VOICE_NOTE_TYPE_CODE = 'VOICE_NOTE'

/** Document type for a photo taken with a declaration. Same graceful skip as
 * the voice note when the organization has not been provisioned with it. */
const INCIDENT_PICTURE_TYPE_CODE = 'INCIDENT_PICTURE'

/** How each kind maps onto the ticket taxonomy (spec 2026-07-18-01). */
const KIND_TICKET: Record<DeclarationKind, {
    type?: components['schemas']['CreateIssueRequest']['type']
    domain?: components['schemas']['CreateIssueRequest']['domain']
    priority: number
}> = {
    // A dégât is a claim in the insurance domain: it reaches the sinistres
    // pipeline, where the 5-working-day declaration clock is watched.
    damage: { type: 'claim', domain: 'insurance', priority: 2 },
    // A panne is service work: it feeds the intervention loop.
    breakdown: { domain: 'service', priority: 3 },
    // A generic incident carries no domain — the syndic qualifies it at triage.
    incident: { priority: 3 },
}

/**
 * The one declaration surface of the product.
 *
 * Everything a declarer can say about a problem, and nothing they cannot: no
 * priority, no assignee, no due date — those are triage decisions the syndic
 * makes afterwards, and asking for them at the kerb is how a declaration gets
 * abandoned. Every field here is optional, including the description: a ticket
 * carrying only "this equipment, at this time, by this person" is already
 * enough to send someone.
 *
 * The undated case is handled rather than rejected: a declaration with no
 * `occurred_at` is stamped with the filing time and flagged `filing_time`, so a
 * reader can tell an observed instant from an assumed one.
 */
export function DeclareIssueDialog({
    open,
    onOpenChange,
    kind,
    targetType,
    targetEntityUid,
    subjectName,
    assetUid,
    declaredFrom,
    onCreated,
}: Props) {
    const { org } = useParams({ strict: false })
    const orgCode = org || 'demo'
    const queryClient = useQueryClient()
    const { t, i18n } = useTranslation('assets')

    const [title, setTitle] = useState('')
    const [occurredAt, setOccurredAt] = useState('')
    const [problemType, setProblemType] = useState<string>('')
    const [cause, setCause] = useState<string>('')
    const [description, setDescription] = useState('')
    const [voiceNote, setVoiceNote] = useState<Blob | null>(null)
    const [photos, setPhotos] = useState<PickedPhoto[]>([])
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    const [transcribing, setTranscribing] = useState(false)
    const [transcriptNotice, setTranscriptNotice] = useState('')
    // What the model heard, kept beside the (possibly corrected) description so
    // a wrong ticket can later be traced to a mis-hearing or a mis-statement.
    const [rawTranscript, setRawTranscript] = useState('')

    const { data: documentTypes = [] } = useDocumentTypes(orgCode)
    const uploadDoc = useUploadDocument(orgCode)
    const createIssue = $api.useMutation('post', '/api/v1/organizations/{orgCode}/issues')

    const reset = () => {
        setTitle('')
        setOccurredAt('')
        setProblemType('')
        setCause('')
        setDescription('')
        setVoiceNote(null)
        setPhotos([])
        setError('')
        setTranscribing(false)
        setTranscriptNotice('')
        setRawTranscript('')
    }

    /**
     * Transcribe the recording that just landed and drop the text into the free
     * field, where the declarer can fix it.
     *
     * It appends rather than replaces: someone who typed a line and then also
     * spoke meant to keep both. A failure is not fatal — the recording is still
     * filed with the declaration — so it only leaves a note saying the audio
     * went through without its text.
     */
    const transcribe = useCallback(async (blob: Blob) => {
        setTranscribing(true)
        setTranscriptNotice('')
        try {
            const fd = new FormData()
            fd.append('file', blob, 'voice-note.webm')
            fd.append('language', i18n.language.startsWith('fr') ? 'fr' : 'en')
            // Through apiClient so the JWT middleware attaches the token; the
            // generated types do not model multipart bodies, hence the cast.
            const res = await (apiClient.POST as unknown as (
                url: string,
                init: { body: FormData },
            ) => Promise<{ response: Response; data?: unknown }>)(
                `/api/v1/organizations/${orgCode}/transcriptions`,
                { body: fd },
            )
            if (!res.response.ok) throw new Error(`HTTP ${res.response.status}`)
            const data = res.data as components['schemas']['TranscriptionResponse']
            if (data.no_speech || !data.text.trim()) {
                setTranscriptNotice(t('declare.transcribe_no_speech'))
                return
            }
            setRawTranscript(data.text)
            setDescription((current) => (current.trim() ? `${current.trim()}\n\n${data.text}` : data.text))
            setTranscriptNotice(t('declare.transcribe_done'))
        } catch (e) {
            console.error('Voice note transcription failed:', e)
            setTranscriptNotice(t('declare.transcribe_failed'))
        } finally {
            setTranscribing(false)
        }
    }, [orgCode, i18n.language, t])

    // The host mounts this closed and learns the subject name later (the asset
    // query resolves after mount), so the state follows `open`, not the mount.
    useEffect(() => {
        if (open) reset()
    }, [open])

    const heading = t(`declare.heading_${kind}`)
    const submitLabel = t('declare.submit')

    const submit = async () => {
        setError('')
        setSubmitting(true)
        try {
            let voiceNoteDocumentUid: string | undefined
            if (voiceNote && assetUid) {
                const extension = voiceNote.type.includes('mp4') ? 'm4a' : 'webm'
                const fd = new FormData()
                fd.append('file', voiceNote, `voice-note.${extension}`)
                fd.append('asset_uid', assetUid)
                fd.append('name', t('declare.voice_document_name', { defaultValue: 'Note vocale' }))
                const voiceType = documentTypes.find((d: { code?: string }) => d?.code === VOICE_NOTE_TYPE_CODE)
                if (voiceType?.uid) fd.append('document_type_uid', voiceType.uid)
                const uploaded = await uploadDoc.mutateAsync(fd as unknown as FormData)
                voiceNoteDocumentUid = (uploaded as { uid?: string })?.uid
            }

            // Photos of the problem. Filed as documents of the asset and
            // pointed at by the ticket — they never touch the asset's reference
            // picture, which answers "is this the right thing?" and would be
            // destroyed by a close-up of a puddle.
            const photoDocumentUids: string[] = []
            if (photos.length > 0 && assetUid) {
                const pictureType = documentTypes.find((d: { code?: string }) => d?.code === INCIDENT_PICTURE_TYPE_CODE)
                for (const photo of photos) {
                    const fd = new FormData()
                    fd.append('file', photo.file, photo.file.name || 'photo.jpg')
                    fd.append('asset_uid', assetUid)
                    fd.append('name', t('declare.photo_document_name'))
                    if (pictureType?.uid) fd.append('document_type_uid', pictureType.uid)
                    const uploaded = await uploadDoc.mutateAsync(fd as unknown as FormData)
                    const uid = (uploaded as { uid?: string })?.uid
                    if (uid) photoDocumentUids.push(uid)
                }
            }

            // Undated declarations are the common case on a phone; "now" beats
            // an empty field, and the flag says which of the two it is.
            const declared = occurredAt ? new Date(occurredAt) : null
            const properties: DeclarationProperties = {
                occurred_at: (declared && !isNaN(declared.getTime()) ? declared : new Date()).toISOString(),
                occurred_at_source: declared && !isNaN(declared.getTime()) ? 'declared' : 'filing_time',
                declared_from: declaredFrom,
                ...(problemType ? { problem_type: problemType as DeclarationProperties['problem_type'] } : {}),
                ...(cause ? { cause: cause as DeclarationProperties['cause'] } : {}),
                ...(voiceNoteDocumentUid ? { voice_note_document_uid: voiceNoteDocumentUid } : {}),
                ...(rawTranscript ? { voice_transcript: rawTranscript } : {}),
                ...(photoDocumentUids.length ? { photo_document_uids: photoDocumentUids } : {}),
            }

            const ticket = KIND_TICKET[kind]
            const resolvedTitle = title.trim()
                || (subjectName ? t(`declare.title_${kind}`, { name: subjectName }) : t(`declare.heading_${kind}`))

            const body: components['schemas']['CreateIssueRequest'] = {
                title: resolvedTitle,
                type: ticket.type ?? 'general',
                priority: ticket.priority,
                properties: properties as unknown as Record<string, never>,
            }
            if (ticket.domain) body.domain = ticket.domain
            if (description.trim()) body.description = description.trim()
            if (targetType === 'asset') body.asset_uid = targetEntityUid
            else {
                body.target_type = targetType
                body.target_entity_uid = targetEntityUid
            }

            const created = await createIssue.mutateAsync({ params: { path: { orgCode } }, body })
            refreshEntityIssues(queryClient, targetEntityUid)
            queryClient.invalidateQueries({
                predicate: (query) => {
                    const k = query.queryKey as unknown[]
                    return k.length >= 2 && k[0] === 'get'
                        && k[1] === '/api/v1/organizations/{orgCode}/issues/stats'
                },
            })
            onCreated?.(created as components['schemas']['Issue'])
            onOpenChange(false)
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e))
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="declare-issue-dialog">
                <DialogHeader>
                    <DialogTitle data-testid="declare-heading">{heading}</DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    {/* A generic incident has no subject to name it after, so it
                        is the only kind that asks for a title. */}
                    {!subjectName && (
                        <div>
                            <Label htmlFor="declare-title">{t('declare.title_label')}</Label>
                            <Input
                                id="declare-title"
                                data-testid="declare-title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                            />
                        </div>
                    )}

                    <div>
                        <Label htmlFor="declare-occurred-at">{t('declare.when')}</Label>
                        <Input
                            id="declare-occurred-at"
                            data-testid="declare-occurred-at"
                            type="datetime-local"
                            value={occurredAt}
                            onChange={(e) => setOccurredAt(e.target.value)}
                        />
                        <p className="mt-1 text-xs text-muted-foreground">{t('declare.when_hint')}</p>
                    </div>

                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <Label>{t('declare.problem_type')}</Label>
                            <Select value={problemType} onValueChange={setProblemType}>
                                <SelectTrigger data-testid="declare-problem-type">
                                    <SelectValue placeholder={t('declare.optional_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {PROBLEM_TYPES.map((code) => (
                                        <SelectItem key={code} value={code} data-testid={`declare-problem-type-${code}`}>
                                            {t(`declare.problem_type_${code}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>{t('declare.cause')}</Label>
                            <Select value={cause} onValueChange={setCause}>
                                <SelectTrigger data-testid="declare-cause">
                                    <SelectValue placeholder={t('declare.optional_placeholder')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {PROBLEM_CAUSES.map((code) => (
                                        <SelectItem key={code} value={code} data-testid={`declare-cause-${code}`}>
                                            {t(`declare.cause_${code}`)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Only assets have a place to file a recording. */}
                    {assetUid && (
                        <div>
                            <Label>{t('declare.voice_note')}</Label>
                            <VoiceNoteRecorder
                                value={voiceNote}
                                onChange={(blob) => {
                                    setVoiceNote(blob)
                                    if (!blob) setTranscriptNotice('')
                                }}
                                onRecorded={transcribe}
                                onNoSpeech={() => setTranscriptNotice(t('declare.transcribe_no_speech'))}
                                transcribing={transcribing}
                                disabled={submitting}
                            />
                            {transcriptNotice && (
                                <p className="mt-1 text-xs text-muted-foreground" data-testid="declare-transcript-notice">
                                    {transcriptNotice}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Photos of the problem — an event, filed on the ticket.
                        Only assets have somewhere to put them. */}
                    {assetUid && (
                        <div>
                            <Label>{t('declare.photos')}</Label>
                            <DeclarationPhotos value={photos} onChange={setPhotos} disabled={submitting} />
                        </div>
                    )}

                    <div>
                        <Label htmlFor="declare-description">{t('declare.free_text')}</Label>
                        <Textarea
                            id="declare-description"
                            data-testid="declare-description"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            placeholder={t('declare.free_text_placeholder')}
                        />
                    </div>

                    {error && (
                        <p className="text-sm text-destructive" data-testid="declare-error">{error}</p>
                    )}
                </div>

                {/* Primary leads, Cancel sits below it — never side by side, so
                    a thumb cannot take one for the other. */}
                <DialogFooter className="flex-col-reverse gap-2 sm:flex-col-reverse sm:justify-start sm:space-x-0">
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={submitting}
                        className="h-11 w-full"
                        data-testid="declare-cancel"
                    >
                        {t('declare.cancel')}
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={submitting}
                        className="h-12 w-full text-base"
                        data-testid="declare-submit"
                    >
                        {submitting ? t('declare.submitting') : submitLabel}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
